import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/lib/supabase";

export default function CaseStudiesPage() {
  const [caseStudies, setCaseStudies] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCaseStudies = async () => {
      try {
        const { data, error } = await supabase
          .schema('raindas_technologies_ltd')
          .from('case_study')
          .select()
          .eq('published', true);

        if (error) throw error;
        setCaseStudies(data ?? []);
      } catch (error) {
        console.error("Error fetching case studies:", error);
        setCaseStudies([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCaseStudies();
  }, []);

  const filterOptions = useMemo(() => {
    const industries = new Set();
    const technologies = new Set();

    caseStudies.forEach((study) => {
      if (study.industry) {
        industries.add(study.industry);
      }

      if (Array.isArray(study.technologies)) {
        study.technologies.forEach((technology) => {
          if (technology) {
            technologies.add(technology);
          }
        });
      }
    });

    return {
      industries: [...industries].sort(),
      technologies: [...technologies].sort(),
    };
  }, [caseStudies]);

  const filteredCaseStudies = useMemo(() => {
    if (selectedFilter === "all") {
      return caseStudies;
    }

    const separatorIndex = selectedFilter.indexOf(":");
    const filterType = selectedFilter.slice(0, separatorIndex);
    const filterValue = selectedFilter.slice(separatorIndex + 1);

    return caseStudies.filter((study) => {
      if (filterType === "industry") {
        return study.industry === filterValue;
      }

      if (filterType === "technology") {
        return Array.isArray(study.technologies) && study.technologies.includes(filterValue);
      }

      return true;
    });
  }, [caseStudies, selectedFilter]);

  return (
    <div className="min-h-screen pt-24" data-testid="case-studies-page">
      {/* Hero */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="case-studies-hero">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-mint mb-6 opacity-0 animate-fade-up">
              Case Studies
            </p>
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-heading font-bold text-white tracking-tighter leading-[0.9] opacity-0 animate-fade-up animate-delay-100">
              Our work
              <br />
              <span className="text-mint">in action.</span>
            </h1>
            <p className="mt-10 text-xl text-slate-400 leading-relaxed opacity-0 animate-fade-up animate-delay-200">
              Explore how we've helped organizations solve complex technical challenges and deliver measurable results.
            </p>
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-16 md:py-24 bg-obsidian-light" data-testid="case-studies-grid">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          {!loading && caseStudies.length > 0 && (
            <div className="mb-10 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">
                Filter case studies
              </p>
              <select
                value={selectedFilter}
                onChange={(event) => setSelectedFilter(event.target.value)}
                className="w-full sm:w-80 bg-obsidian border border-white/10 px-4 py-3 text-sm text-white outline-none transition-colors hover:border-mint/40 focus:border-mint"
                data-testid="case-study-filter"
              >
                <option value="all">All case studies</option>
                {filterOptions.industries.length > 0 && (
                  <optgroup label="Industry">
                    {filterOptions.industries.map((industry) => (
                      <option key={`industry-${industry}`} value={`industry:${industry}`}>
                        {industry}
                      </option>
                    ))}
                  </optgroup>
                )}
                {filterOptions.technologies.length > 0 && (
                  <optgroup label="Technology">
                    {filterOptions.technologies.map((technology) => (
                      <option key={`technology-${technology}`} value={`technology:${technology}`}>
                        {technology}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white/5 border border-white/5 p-10">
                  <Skeleton className="h-4 w-24 bg-white/10 mb-6" />
                  <Skeleton className="h-8 w-3/4 bg-white/10 mb-4" />
                  <Skeleton className="h-24 w-full bg-white/10" />
                </div>
              ))}
            </div>
          ) : caseStudies.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-slate-400 text-xl">No case studies available yet.</p>
            </div>
          ) : filteredCaseStudies.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-slate-400 text-xl">No case studies match that filter.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredCaseStudies.map((study, idx) => (
                <Link
                  key={study.id}
                  to={`/case-studies/${study.slug}`}
                  className={`card-beam p-10 group ${idx === 0 ? 'lg:col-span-2' : ''}`}
                  data-testid={`case-study-card-${study.slug}`}
                >
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-xs font-mono uppercase tracking-wider text-slate-600">
                      {study.industry}
                    </span>
                    {study.featured && (
                      <span className="text-xs font-mono uppercase tracking-wider px-3 py-1 bg-mint/10 text-mint border border-mint/20">
                        Featured
                      </span>
                    )}
                  </div>

                  <h2 className={`${idx === 0 ? 'text-4xl' : 'text-2xl'} font-heading font-bold text-white group-hover:text-mint transition-colors mb-4 tracking-tight`}>
                    {study.title}
                  </h2>

                  <p className="text-sm text-slate-500 mb-6">
                    Client: {study.client}
                  </p>

                  <p className={`text-slate-400 leading-relaxed mb-8 ${idx === 0 ? 'text-lg max-w-3xl' : 'line-clamp-3'}`}>
                    {study.challenge}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-8">
                    {study.technologies.slice(0, idx === 0 ? 6 : 4).map((tech) => (
                      <span
                        key={tech}
                        className="text-xs px-3 py-1 bg-white/5 text-slate-400 border border-white/5"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center gap-2 text-mint group-hover:text-white transition-colors">
                    <span className="text-sm font-medium">Read Case Study</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 border-t border-white/5" data-testid="case-studies-cta">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white tracking-tight">
            Have a similar challenge?
          </h2>
          <p className="mt-6 text-xl text-slate-400 max-w-xl mx-auto">
            Let us know about your project. We'll provide an assessment and discuss how we can help.
          </p>
          <Link to="/contact">
            <button
              data-testid="case-studies-contact-btn"
              className="mt-10 btn-primary group flex items-center gap-3 bg-mint text-black px-10 py-5 font-bold text-sm tracking-wide uppercase hover:bg-white transition-colors mx-auto"
            >
              Discuss Your Project
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}
